import React, { useState, useEffect } from 'react';
import {
  Plus, Edit, Trash2, Search, AlertCircle, CheckCircle, Clock,
  X, Save, Filter, Download, Image as ImageIcon, MapPin, AlertTriangle, Truck,
  Eye, Camera, Building2, User, Calendar, FileText, Wrench, Phone, Mail, History,
  Package, RotateCcw, ArrowLeftRight
} from 'lucide-react';
import * as XLSX from 'xlsx';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

interface CameraFault {
  id: number;
  camera_id: number | null;
  camera_name: string;
  camera_brand?: string;
  camera_model?: string;
  camera_screenshot_path?: string | null;
  ip_address: string | null;
  location_info: string | null;
  location_name?: string;
  location_address?: string;
  nvr_id?: number;
  nvr_name?: string;
  fault_description: string;
  image_path: string | null;
  reported_by_user_id: number;
  reported_by_username?: string;
  reported_by_name: string;
  reported_by_email?: string;
  reported_at: string;
  status: 'açık' | 'inceleniyor' | 'çözümlendi' | 'kapalı' | 'konsinye bırakıldı';
  consignment_record_id?: number | null;
  consignment_camera_name?: string | null;
  consignment_camera_brand?: string | null;
  consignment_camera_model?: string | null;
  consignment_camera_id?: number | null;
  consignment_assigned_at?: string | null;
  consignment_assigned_by?: string | null;
  notes: string | null;
  resolved_at: string | null;
  resolved_by_user_id: number | null;
  resolved_by_username?: string;
  supplier_id?: number | null;
  supplier_name?: string | null;
  assigned_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface Supplier {
  id: number;
  name: string;
  service_categories: string;
}

interface Camera {
  id: number;
  brand: string;
  model: string;
  alias?: string;
  ip_address: string;
  nvr_id: number;
  nvr_name: string;
  screenshot_path?: string;
}

interface NVR {
  id: number;
  name: string;
  ip_address: string;
  location_name: string;
}

interface Location {
  id: number;
  name: string;
  address: string;
  phone: string;
}

interface SupplierAction {
  id: number;
  camera_fault_id: number;
  supplier_user_id: number;
  supplier_id: number;
  action_type: string;
  old_status: string | null;
  new_status: string | null;
  description: string | null;
  solution_text: string | null;
  file_path: string | null;
  file_name: string | null;
  is_visible_to_customer: boolean;
  created_at: string;
  supplier_user_name: string | null;
  supplier_user_email: string | null;
  supplier_company_name: string | null;
}

interface FaultLog {
  id: number;
  camera_fault_id: number;
  user_id: number | null;
  user_type: 'admin' | 'user' | 'supplier';
  supplier_user_id: number | null;
  action_type: string;
  old_value: string | null;
  new_value: string | null;
  description: string | null;
  user_name: string | null;
  created_at: string;
  admin_user_name: string | null;
  supplier_user_name_from_table: string | null;
}

interface ConsignmentRecord {
  id: number;
  camera_fault_id: number;
  faulty_camera_id: number | null;
  faulty_camera_name: string;
  consignment_camera_id: number | null;
  consignment_camera_name: string;
  consignment_camera_brand: string | null;
  consignment_camera_model: string | null;
  consignment_camera_ip: string | null;
  consignment_camera_inventory: string | null;
  consignment_notes: string | null;
  assigned_at: string;
  assigned_by_name: string | null;
  returned_at: string | null;
  returned_by_name: string | null;
  return_notes: string | null;
  status: 'aktif' | 'iade edildi';
  con_alias: string | null;
  con_screenshot: string | null;
  con_nvr_name: string | null;
  con_location_name: string | null;
}

interface FaultDetails {
  fault: CameraFault & {
    channel_number?: number;
    nvr_ip?: string;
    location_phone?: string;
    supplier_phone?: string;
    supplier_email?: string;
    supplier_address?: string;
    supplier_contact_person?: string;
    camera_screenshot_path?: string;
  };
  supplierActions: SupplierAction[];
  logs: FaultLog[];
  consignmentRecords: ConsignmentRecord[];
  solution: {
    description: string | null;
    solutionText: string | null;
    resolvedAt: string;
    resolvedBy: string | null;
    filePath: string | null;
    fileName: string | null;
  } | null;
}

const statusColors: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  'açık': { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-100', icon: <AlertTriangle className="w-4 h-4" /> },
  'inceleniyor': { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-100', icon: <Clock className="w-4 h-4" /> },
  'çözümlendi': { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-100', icon: <CheckCircle className="w-4 h-4" /> },
  'kapalı': { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-200 dark:text-gray-100', icon: <CheckCircle className="w-4 h-4" /> },
  'konsinye bırakıldı': { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-100', icon: <Package className="w-4 h-4" /> }
};

const CameraFaultsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [faults, setFaults] = useState<CameraFault[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [nvrs, setNvrs] = useState<NVR[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [editingFault, setEditingFault] = useState<CameraFault | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [currentUsername, setCurrentUsername] = useState('');
  const [currentUserLocationId, setCurrentUserLocationId] = useState<number | null>(null);
  const [selectedImageForModal, setSelectedImageForModal] = useState<string | null>(null);
  const [currentUserLocation, setCurrentUserLocation] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedFormLocation, setSelectedFormLocation] = useState<number | null>(null);
  const [selectedFormNvr, setSelectedFormNvr] = useState<number | null>(null);
  const [cameraSearchQuery, setCameraSearchQuery] = useState('');
  const [showCameraDropdown, setShowCameraDropdown] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [supplierNotes, setSupplierNotes] = useState('');
  const [cardFilter, setCardFilter] = useState<string>('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [faultDetails, setFaultDetails] = useState<FaultDetails | null>(null);

  // Konsinye states
  const [showConsignmentModal, setShowConsignmentModal] = useState(false);
  const [consignmentFault, setConsignmentFault] = useState<CameraFault | null>(null);
  const [consignmentSearch, setConsignmentSearch] = useState('');
  const [selectedConsignmentCamera, setSelectedConsignmentCamera] = useState<Camera | null>(null);
  const [consignmentNotes, setConsignmentNotes] = useState('');
  const [consignmentSubmitting, setConsignmentSubmitting] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnFault, setReturnFault] = useState<CameraFault | null>(null);
  const [returnNotes, setReturnNotes] = useState('');
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    location_id: '',
    camera_id: '',
    camera_name: '',
    ip_address: '',
    location_info: '',
    fault_description: 'Görüntü Gelmiyor',
    reported_by_name: '',
    status: 'açık',
    notes: ''
  });

  // Arıza açıklaması seçenekleri (value = DB value, label = translated display)
  const faultDescriptionOptions = [
    { value: 'Görüntü Gelmiyor', label: t('camera.faultDesc1') },
    { value: 'Görüntü Gelip Gidiyor', label: t('camera.faultDesc2') },
    { value: 'Kamera Merceği Temizlenmeli', label: t('camera.faultDesc3') },
    { value: 'Haberleşme Kablosu Arızalı', label: t('camera.faultDesc4') },
    { value: 'Diğer', label: t('camera.faultDesc5') },
  ];

  useEffect(() => {
    // Oturum açan kullanıcı bilgisini al
    const savedUser = localStorage.getItem('auth_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setCurrentUsername(userData.username || userData.name || '');
        // Kullanıcının lokasyonunu al
        if (userData.location && userData.location.name) {
          setCurrentUserLocation(userData.location.name);
        }
        // Kullanıcının lokasyon ID'sini al
        if (userData.location_id) {
          setCurrentUserLocationId(userData.location_id);
        }
      } catch (error) {
        console.error('Kullanıcı bilgisi alınamadı:', error);
      }
    }

    fetchLocations();
    fetchFaults();
    fetchCameras();
    fetchNvrs();
    fetchSuppliers();
  }, []);

  const fetchFaults = async () => {
    try {
      const response = await fetch('/api/camera-faults', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFaults(data.data || []);
      } else {
        const errorData = await response.json();
        console.error('API Hatası:', response.status, errorData);
        // 403 Forbidden ise, permission sorunu olabilir
        if (response.status === 403) {
          console.warn('Camera-faults menüsüne erişim izniniz yok');
        }
      }
    } catch (error) {
      console.error('Arızalar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCameras = async () => {
    try {
      const response = await fetch('/api/cameras', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCameras(data.data || []);
      }
    } catch (error) {
      console.error('Kameralar yüklenemedi:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch('/api/locations', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data.data || []);
      }
    } catch (error) {
      console.error('Lokasyonlar yüklenemedi:', error);
    }
  };

  const fetchNvrs = async () => {
    try {
      const response = await fetch('/api/nvrs', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNvrs(data.data || []);
      }
    } catch (error) {
      console.error('NVR\'lar yüklenemedi:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch('/api/suppliers', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Sadece Güvenlik Kamera Sistemleri kategorisindeki tedarikçileri filtrele
        const cameraSuppliers = (data.data || []).filter((supplier: Supplier) => {
          try {
            const categories = JSON.parse(supplier.service_categories || '[]');
            return categories.includes('Güvenlik Kamera Sistemleri');
          } catch {
            return false;
          }
        });
        setSuppliers(cameraSuppliers);
      }
    } catch (error) {
      console.error('Tedarikçiler yüklenemedi:', error);
    }
  };

  const fetchFaultDetails = async (faultId: number) => {
    setDetailLoading(true);
    setShowDetailModal(true);
    try {
      const response = await fetch(`/api/camera-faults/${faultId}/details`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFaultDetails(data.data);
      } else {
        console.error('Arıza detayları yüklenemedi');
        alert(t('camera.alertDetailsError'));
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Arıza detayları yüklenemedi:', error);
      alert(t('camera.alertDetailsErrorLoad'));
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // Konsinye kamera atama
  const handleOpenConsignment = (fault: CameraFault) => {
    setConsignmentFault(fault);
    setSelectedConsignmentCamera(null);
    setConsignmentSearch('');
    setConsignmentNotes('');
    setShowConsignmentModal(true);
  };

  const submitConsignment = async () => {
    if (!consignmentFault || !selectedConsignmentCamera) {
      alert(t('camera.alertSelectConsignment'));
      return;
    }
    setConsignmentSubmitting(true);
    try {
      const res = await fetch(`/api/camera-faults/${consignmentFault.id}/consignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          consignment_camera_id: selectedConsignmentCamera.id,
          notes: consignmentNotes || undefined
        })
      });
      if (res.ok) {
        alert(t('camera.alertConsignmentSaved'));
        setShowConsignmentModal(false);
        setConsignmentFault(null);
        fetchFaults();
      } else {
        const err = await res.json();
        alert(err.error || t('camera.alertConsignmentError'));
      }
    } catch (e) {
      alert(t('camera.alertConnectionError'));
    } finally {
      setConsignmentSubmitting(false);
    }
  };

  // Konsinye iade al
  const handleOpenReturn = (fault: CameraFault) => {
    setReturnFault(fault);
    setReturnNotes('');
    setShowReturnModal(true);
  };

  const submitReturn = async () => {
    if (!returnFault) return;
    setReturnSubmitting(true);
    try {
      const res = await fetch(`/api/camera-faults/${returnFault.id}/consignment/return`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ return_notes: returnNotes || undefined })
      });
      if (res.ok) {
        alert(t('camera.alertReturnSuccess'));
        setShowReturnModal(false);
        setReturnFault(null);
        fetchFaults();
      } else {
        const err = await res.json();
        alert(err.error || t('camera.alertConsignmentError'));
      }
    } catch (e) {
      alert(t('camera.alertConnectionError'));
    } finally {
      setReturnSubmitting(false);
    }
  };

  const handleAssignSupplier = async (fault: CameraFault) => {
    setEditingFault(fault);
    setSelectedSupplier(fault.supplier_id || null);
    setSupplierNotes('');
    setShowSupplierModal(true);
  };

  const submitAssignSupplier = async () => {
    if (!editingFault || !selectedSupplier) {
      alert(t('camera.alertSelectSupplier'));
      return;
    }

    try {
      const response = await fetch(`/api/camera-faults/${editingFault.id}/assign-supplier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          supplier_id: selectedSupplier,
          notes: supplierNotes
        })
      });

      if (response.ok) {
        alert(t('camera.alertSupplierAssigned'));
        setShowSupplierModal(false);
        setEditingFault(null);
        setSelectedSupplier(null);
        setSupplierNotes('');
        fetchFaults();
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('camera.alertSupplierError'));
      }
    } catch (error) {
      console.error('Tedarikçi atama hatası:', error);
      alert(t('camera.alertSupplierAssignError'));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        return data.filename;
      }
    } catch (error) {
      console.error('Resim yükleme hatası:', error);
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.camera_name || !formData.fault_description) {
      alert(t('camera.alertCameraNameRequired'));
      return;
    }

    try {
      let imagePath = null;
      if (uploadedImage) {
        imagePath = await uploadImage(uploadedImage);
      }

      const url = editingFault ? `/api/camera-faults/${editingFault.id}` : '/api/camera-faults';
      const method = editingFault ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        image_path: imagePath || (editingFault?.image_path || null)
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchFaults();
        setShowModal(false);
        resetForm();
        alert(editingFault ? t('camera.alertUpdated') : t('camera.alertAdded'));
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('camera.alertError'));
      }
    } catch (error) {
      console.error('Arıza kaydedilemedi:', error);
      alert(t('camera.alertError'));
    }
  };

  const handleEdit = (fault: CameraFault) => {
    setEditingFault(fault);
    setFormData({
      camera_id: fault.camera_id?.toString() || '',
      camera_name: fault.camera_name,
      ip_address: fault.ip_address || '',
      location_info: fault.location_info || '',
      fault_description: fault.fault_description,
      reported_by_name: fault.reported_by_name,
      status: fault.status,
      notes: fault.notes || ''
    });

    // Debug: Log the image_path value
    console.log('Editing fault, image_path:', fault.image_path);

    if (fault.image_path) {
      const imageUrl = `/uploads/${fault.image_path}`;
      console.log('Setting preview image URL:', imageUrl);
      setPreviewImage(imageUrl);
    } else {
      console.warn('Fault has no image_path:', fault.id);
      setPreviewImage(null);
    }
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (confirm(t('camera.confirmDelete'))) {
      try {
        const response = await fetch(`/api/camera-faults/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });

        if (response.ok) {
          fetchFaults();
          alert(t('camera.alertDeleted'));
        }
      } catch (error) {
        console.error('Arıza silinemedi:', error);
        alert(t('camera.alertError'));
      }
    }
  };

  // Seçilen lokasyonun NVR'larını al
  const getNvrsForLocation = (locationId: number): NVR[] => {
    return nvrs.filter(n => {
      const nvrLocation = locations.find(l => l.name === n.location_name);
      return nvrLocation?.id === locationId;
    });
  };

  // Seçilen NVR'ın kameralarını al
  const getCamerasForNvr = (nvrId: number): Camera[] => {
    return cameras.filter(c => c.nvr_id === nvrId);
  };

  // Kamera araması
  const getFilteredCameras = (): Camera[] => {
    if (!selectedFormNvr) return [];
    const nvrCameras = getCamerasForNvr(selectedFormNvr);
    if (!cameraSearchQuery.trim()) return nvrCameras;

    const query = cameraSearchQuery.toLowerCase();
    return nvrCameras.filter(camera => {
      const brandModel = `${String(camera.brand || '')} ${String(camera.model || '')}`.toLowerCase();
      const ip = String(camera.ip_address || '').toLowerCase();
      return brandModel.includes(query) || ip.includes(query);
    });
  };

  const resetForm = () => {
    // Kullanıcının lokasyonunun ID'sini bul
    let userLocationId = '';
    if (currentUserLocation && locations.length > 0) {
      const userLocation = locations.find(loc => loc.name === currentUserLocation);
      if (userLocation) {
        userLocationId = userLocation.id.toString();
      }
    } else if (currentUserLocationId) {
      userLocationId = currentUserLocationId.toString();
    }

    setFormData({
      location_id: userLocationId,
      camera_id: '',
      camera_name: '',
      ip_address: '',
      location_info: '',
      fault_description: 'Görüntü Gelmiyor',
      reported_by_name: currentUsername,
      status: 'açık',
      notes: ''
    });
    setSelectedFormLocation(userLocationId ? parseInt(userLocationId) : null);
    setSelectedFormNvr(null);
    setEditingFault(null);
    setPreviewImage(null);
    setUploadedImage(null);
    setCameraSearchQuery('');
    setShowCameraDropdown(false);
  };

  const exportToExcel = () => {
    const data = filteredFaults.map(fault => ({
      [t('camera.excelCameraName')]: fault.camera_name,
      [t('camera.excelIpAddress')]: fault.ip_address || '-',
      [t('camera.excelLocation')]: fault.location_info || '-',
      [t('camera.excelFault')]: fault.fault_description,
      [t('camera.excelStatus')]: fault.status,
      [t('camera.excelDate')]: new Date(fault.reported_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR'),
      [t('camera.excelReporter')]: fault.reported_by_name,
      [t('camera.excelNotes')]: fault.notes || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('camera.excelSheet'));
    XLSX.writeFile(wb, `kamera_arizelari_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.xlsx`);
  };

  // Lokasyonlara göre arıza sayısını hesapla
  const getLocationStats = () => {
    const stats: { [key: string]: number } = {};
    faults.forEach(fault => {
      if (fault.location_info) {
        stats[fault.location_info] = (stats[fault.location_info] || 0) + 1;
      }
    });
    return Object.entries(stats)
      .map(([location, count]) => ({ location, count }))
      .sort((a, b) => b.count - a.count);
  };

  const locationStats = getLocationStats();
  // Toplam arıza: sadece çözümlenmemiş arızaları say (açık ve inceleniyor)
  const totalFaults = faults.filter(f => f.status !== 'çözümlendi' && f.status !== 'kapalı').length;

  const filteredFaults = faults.filter(fault => {
    const matchesSearch = !searchTerm || (
      String(fault.camera_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(fault.fault_description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(fault.location_info || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(fault.ip_address || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesLocation = !locationFilter || fault.location_info === locationFilter;

    // Kart filtresi kontrolü - kart filtresi aktifse status filtresini geçersiz kılar
    let matchesCardFilter = true;
    let matchesStatus = true;

    if (cardFilter === 'total') {
      // Toplam arıza: sadece çözümlenmemiş arızalar
      matchesCardFilter = fault.status !== 'çözümlendi' && fault.status !== 'kapalı';
    } else if (cardFilter === 'open') {
      matchesCardFilter = fault.status === 'açık';
    } else if (cardFilter === 'resolved') {
      matchesCardFilter = fault.status === 'çözümlendi' || fault.status === 'kapalı';
    } else if (cardFilter === 'top-location' && locationStats.length > 0) {
      matchesCardFilter = fault.location_info === locationStats[0].location;
    } else {
      // Kart filtresi aktif değilse normal status filtresini uygula
      matchesStatus = statusFilter === 'all'
        ? true
        : statusFilter === 'active'
          ? (fault.status !== 'çözümlendi' && fault.status !== 'kapalı')
          : fault.status === statusFilter;
    }

    return matchesSearch && matchesStatus && matchesLocation && matchesCardFilter;
  });

  const totalItems = filteredFaults.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFaults = filteredFaults.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, cardFilter]);

  // Bildirimden belirli bir kamera arızası açma
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail?.page !== 'camera-faults' || !e.detail?.entityId) return;
      fetchFaultDetails(e.detail.entityId);
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{t('camera.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">{t('camera.subtitle')}</p>
        </div>
        <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center justify-center px-2.5 sm:px-4 py-2 min-h-[44px] rounded-lg transition-colors text-sm sm:text-base ${
              showFilters ? 'bg-purple-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            <Filter className="w-4 h-4 mr-1 sm:mr-2" />
            {t('camera.btnFilter')}
          </button>
          <button
            onClick={exportToExcel}
            className="flex items-center justify-center px-2.5 sm:px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
          >
            <Download className="w-4 h-4 mr-1 sm:mr-2" />
            Excel
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center justify-center px-2.5 sm:px-4 py-2 min-h-[44px] bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            {t('camera.btnNewFault')}
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {/* Total Faults Card */}
        <button
          type="button"
          onClick={() => setCardFilter(cardFilter === 'total' ? '' : 'total')}
          className={`w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 p-3 sm:p-4 cursor-pointer transition-all hover:shadow-md ${
            cardFilter === 'total'
              ? 'border-orange-500 ring-2 ring-orange-200 dark:ring-orange-800'
              : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">{t('camera.statTotal')}</p>
              <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">{totalFaults}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('camera.statTotalSub')}</p>
            </div>
            <div className="hidden sm:block text-red-500 opacity-20">
              <AlertTriangle className="w-12 h-12" />
            </div>
          </div>
          {cardFilter === 'total' && (
            <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 font-medium">{t('camera.filterActive')}</div>
          )}
        </button>

        {/* Open Faults Card */}
        <button
          type="button"
          onClick={() => setCardFilter(cardFilter === 'open' ? '' : 'open')}
          className={`w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 p-3 sm:p-4 cursor-pointer transition-all hover:shadow-md ${
            cardFilter === 'open'
              ? 'border-red-500 ring-2 ring-red-200 dark:ring-red-800'
              : 'border-gray-200 dark:border-gray-700 hover:border-red-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">{t('camera.statOpen')}</p>
              <p className="text-xl sm:text-3xl font-bold text-red-600 dark:text-red-400 mt-1">
                {faults.filter(f => f.status === 'açık').length}
              </p>
            </div>
            <div className="hidden sm:block text-red-500 opacity-20">
              <AlertTriangle className="w-12 h-12" />
            </div>
          </div>
          {cardFilter === 'open' && (
            <div className="mt-2 text-xs text-red-600 dark:text-red-400 font-medium">{t('camera.filterActive')}</div>
          )}
        </button>

        {/* Resolved Faults Card */}
        <button
          type="button"
          onClick={() => setCardFilter(cardFilter === 'resolved' ? '' : 'resolved')}
          className={`w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 p-3 sm:p-4 cursor-pointer transition-all hover:shadow-md ${
            cardFilter === 'resolved'
              ? 'border-green-500 ring-2 ring-green-200 dark:ring-green-800'
              : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">{t('camera.statResolved')}</p>
              <p className="text-xl sm:text-3xl font-bold text-green-600 dark:text-green-400 mt-1">
                {faults.filter(f => f.status === 'çözümlendi' || f.status === 'kapalı').length}
              </p>
            </div>
            <div className="hidden sm:block text-green-500 opacity-20">
              <CheckCircle className="w-12 h-12" />
            </div>
          </div>
          {cardFilter === 'resolved' && (
            <div className="mt-2 text-xs text-green-600 dark:text-green-400 font-medium">{t('camera.filterActive')}</div>
          )}
        </button>

        {/* Top Location Card */}
        <button
          type="button"
          onClick={() => setCardFilter(cardFilter === 'top-location' ? '' : 'top-location')}
          disabled={locationStats.length === 0}
          className={`w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 p-3 sm:p-4 cursor-pointer transition-all hover:shadow-md ${
            cardFilter === 'top-location'
              ? 'border-primary ring-2 ring-primary/30 dark:ring-primary-dark'
              : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">{t('camera.statTopLocation')}</p>
              <p className="text-sm sm:text-base font-bold text-primary dark:text-primary mt-1 break-words">
                {locationStats.length > 0 ? locationStats[0].location : t('camera.statNoData')}
              </p>
              {locationStats.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('camera.statFaultCount', { count: locationStats[0].count })}
                </p>
              )}
            </div>
            <div className="hidden sm:block text-primary opacity-20">
              <MapPin className="w-12 h-12" />
            </div>
          </div>
          {cardFilter === 'top-location' && (
            <div className="mt-2 text-xs text-primary dark:text-primary font-medium">{t('camera.filterActive')}</div>
          )}
        </button>
      </div>

      {/* Arama */}
      <div className="mb-4 sm:mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('camera.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Filtreler */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">{t('camera.filtersTitle')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('camera.filterStatusLabel')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="active">{t('camera.filterStatusActive')}</option>
                <option value="all">{t('camera.filterStatusAll')}</option>
                <option value="açık">{t('camera.filterStatusOpen')}</option>
                <option value="inceleniyor">{t('camera.filterStatusReviewing')}</option>
                <option value="konsinye bırakıldı">{t('camera.filterStatusConsignment')}</option>
                <option value="çözümlendi">{t('camera.filterStatusResolved')}</option>
                <option value="kapalı">{t('camera.filterStatusClosed')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('camera.filterLocationLabel')}
              </label>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('camera.filterAllLocations')}</option>
                {locationStats.map((stat) => (
                  <option key={stat.location} value={stat.location}>
                    {stat.location} ({stat.count})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Arızalar Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {paginatedFaults.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">{t('camera.noFaults')}</p>
          </div>
        ) : (
          <>
            {/* Masaüstü Tablo */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full table-auto divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-8">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('camera.colCamera')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('camera.colFault')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('camera.colStatus')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('camera.colDate')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('camera.colReporter')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('camera.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedFaults.map((fault) => {
                    const statusStyle = statusColors[fault.status];
                    const hasScreenshot = !!fault.camera_screenshot_path;
                    const hasFaultImage = !!fault.image_path;
                    return (
                      <React.Fragment key={fault.id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 w-8">#{fault.id}</td>
                          <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                            <button
                              onClick={() => hasScreenshot
                                ? setSelectedImageForModal(`/uploads/${fault.camera_screenshot_path}`)
                                : hasFaultImage
                                  ? setSelectedImageForModal(`/uploads/${fault.image_path}`)
                                  : undefined
                              }
                              className={`text-left w-full ${(hasScreenshot || hasFaultImage) ? 'cursor-pointer hover:text-primary transition-colors group' : 'cursor-default'}`}
                              disabled={!hasScreenshot && !hasFaultImage}
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold leading-tight">{fault.camera_name}</span>
                                {hasScreenshot && (
                                  <Camera className="w-3.5 h-3.5 text-green-500 flex-shrink-0 opacity-70 group-hover:opacity-100" title="Kamera görüntüsü mevcut" />
                                )}
                                {!hasScreenshot && hasFaultImage && (
                                  <ImageIcon className="w-3.5 h-3.5 text-orange-400 flex-shrink-0 opacity-70 group-hover:opacity-100" title="Arıza fotoğrafı mevcut" />
                                )}
                              </div>
                              {fault.camera_brand && fault.camera_model && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{fault.camera_brand} {fault.camera_model}</div>
                              )}
                              {fault.location_name && (
                                <div className="text-xs text-gray-400 dark:text-gray-500">{fault.location_name}</div>
                              )}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-[180px]">
                            <div className="flex items-start gap-1">
                              <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                              <span className="text-xs leading-tight">{fault.fault_description}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                              {statusStyle.icon}
                              {fault.status.charAt(0).toUpperCase() + fault.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {new Date(fault.reported_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-[120px] truncate">
                            {fault.reported_by_name}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-0.5 justify-end">
                              <button
                                onClick={() => fetchFaultDetails(fault.id)}
                                className="p-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900 rounded transition-colors"
                                title={t('camera.titleDetails')}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleAssignSupplier(fault)}
                                className={`p-1.5 ${fault.supplier_id ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'} hover:bg-orange-50 dark:hover:bg-orange-900 rounded transition-colors`}
                                title={fault.supplier_id ? t('camera.titleSupplierAssigned', { name: fault.supplier_name || '' }) : t('camera.titleAssignSupplier')}
                              >
                                {fault.supplier_id ? <CheckCircle className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                              </button>
                              {fault.status === 'konsinye bırakıldı' ? (
                                <button
                                  onClick={() => handleOpenReturn(fault)}
                                  className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                                  title={t('camera.titleReturnConsignment', { name: fault.consignment_camera_name || '' })}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              ) : (fault.status === 'açık' || fault.status === 'inceleniyor') ? (
                                <button
                                  onClick={() => handleOpenConsignment(fault)}
                                  className="p-1.5 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900 rounded transition-colors"
                                  title={t('camera.titleConsignment')}
                                >
                                  <Package className="w-4 h-4" />
                                </button>
                              ) : null}
                              <button
                                onClick={() => handleEdit(fault)}
                                className="p-1.5 text-primary hover:bg-primary/10 dark:hover:bg-primary/30 rounded transition-colors"
                                title={t('common.edit')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(fault.id)}
                                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded transition-colors"
                                title={t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobil Kart Listesi */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedFaults.map((fault) => {
                const statusStyle = statusColors[fault.status];
                const hasScreenshot = !!fault.camera_screenshot_path;
                const hasFaultImage = !!fault.image_path;
                const previewImg = hasScreenshot
                  ? `/uploads/${fault.camera_screenshot_path}`
                  : hasFaultImage ? `/uploads/${fault.image_path}` : null;

                return (
                  <div key={fault.id} className="p-3">
                    {/* Üst satır: görsel + bilgiler */}
                    <div className="flex gap-3">
                      {/* Görsel küçük önizleme */}
                      {previewImg ? (
                        <button
                          onClick={() => setSelectedImageForModal(previewImg)}
                          className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:opacity-80 transition-opacity"
                        >
                          <img
                            src={previewImg}
                            alt="Kamera"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ) : (
                        <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center border border-gray-200 dark:border-gray-600">
                          <Camera className="w-6 h-6 text-gray-400" />
                        </div>
                      )}

                      {/* Bilgiler */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">{fault.camera_name}</p>
                            {fault.camera_brand && fault.camera_model && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">{fault.camera_brand} {fault.camera_model}</p>
                            )}
                            {fault.location_name && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">{fault.location_name}</p>
                            )}
                          </div>
                          <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                            {statusStyle.icon}
                            {fault.status.charAt(0).toUpperCase() + fault.status.slice(1)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <AlertTriangle className="w-3 h-3 text-orange-500 flex-shrink-0" />
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{fault.fault_description}</p>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(fault.reported_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')} · {fault.reported_by_name}
                          </p>
                          <span className="text-xs text-gray-400 dark:text-gray-500">#{fault.id}</span>
                        </div>
                      </div>
                    </div>

                    {/* Alt işlem butonları */}
                    <div className="flex flex-wrap items-center gap-1 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={() => fetchFaultDetails(fault.id)}
                        className="flex items-center gap-1.5 px-3 min-h-[44px] text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {t('camera.btnDetails')}
                      </button>
                      <button
                        onClick={() => handleAssignSupplier(fault)}
                        className={`flex items-center gap-1.5 px-3 min-h-[44px] text-sm ${fault.supplier_id ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'} hover:bg-orange-50 dark:hover:bg-orange-900 rounded-lg transition-colors`}
                      >
                        {fault.supplier_id ? <CheckCircle className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                        {fault.supplier_id ? t('camera.btnSupplierAssigned') : t('camera.btnAssignSupplier')}
                      </button>
                      {fault.status === 'konsinye bırakıldı' ? (
                        <button
                          onClick={() => handleOpenReturn(fault)}
                          className="flex items-center gap-1.5 px-3 min-h-[44px] text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition-colors"
                        >
                          <RotateCcw className="w-4 h-4" />
                          {t('camera.btnReturnConsignment')}
                        </button>
                      ) : (fault.status === 'açık' || fault.status === 'inceleniyor') ? (
                        <button
                          onClick={() => handleOpenConsignment(fault)}
                          className="flex items-center gap-1.5 px-3 min-h-[44px] text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900 rounded-lg transition-colors"
                        >
                          <Package className="w-4 h-4" />
                          {t('camera.btnConsignment')}
                        </button>
                      ) : null}
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(fault)}
                          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-primary hover:bg-primary/10 dark:hover:bg-primary/30 rounded-lg transition-colors"
                          title={t('common.edit')}
                          aria-label={t('common.edit')}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(fault.id)}
                          className="ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition-colors"
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <>
          <div className="sm:hidden mt-4 flex items-center justify-between gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('camera.pagePrev')}
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('camera.pageOf', { current: currentPage, total: totalPages })}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('camera.pageNext')}
            </button>
          </div>
          <div className="hidden sm:flex mt-6 flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('camera.pageInfo', { start: startIndex + 1, end: Math.min(endIndex, totalItems), total: totalItems })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('camera.pagePrev')}
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Gösterilen 5 sayfa toplam sayfa sayısını aşmasın
                const firstPage = Math.min(Math.max(currentPage - 2, 1), Math.max(totalPages - 4, 1));
                const pageNum = firstPage + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === pageNum
                        ? 'bg-primary text-white'
                        : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('camera.pageNext')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Resim Görüntüleme Modal */}
      {selectedImageForModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
          onClick={() => setSelectedImageForModal(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImageForModal(null)}
              className="absolute top-4 right-4 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 z-10"
            >
              <X className="w-6 h-6 text-gray-800 dark:text-gray-200" />
            </button>
            <img
              src={selectedImageForModal}
              alt="Tam Boyut Resim"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Form Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div
            className="bg-white dark:bg-gray-800 rounded-lg px-4 sm:px-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 sm:py-4 mb-4 flex justify-between items-center bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                {editingFault ? t('camera.formTitleEdit') : t('camera.formTitleNew')}
              </h2>
              <button type="button" onClick={() => setShowModal(false)} className="-mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={t('common.close')}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Lokasyon Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('camera.formLabelLocation')}
                  </label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => {
                      const locationId = parseInt(e.target.value);
                      setFormData({...formData, location_id: e.target.value});
                      setSelectedFormLocation(locationId || null);
                      setSelectedFormNvr(null);
                      setFormData(prev => ({...prev, camera_id: '', camera_name: '', ip_address: ''}));
                    }}
                    className="w-full px-3 py-2 border-2 border-primary/50 dark:border-primary rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white bg-primary/10 dark:bg-primary/90"
                  >
                    <option value="">{t('camera.formSelectLocation')}</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* NVR Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    📡 NVR {selectedFormLocation ? '' : t('camera.formNvrSelectLocation')}
                  </label>
                  <select
                    value={selectedFormNvr || ''}
                    onChange={(e) => {
                      const nvrId = parseInt(e.target.value);
                      setSelectedFormNvr(nvrId || null);
                      setFormData(prev => ({...prev, camera_id: '', camera_name: '', ip_address: ''}));
                      setCameraSearchQuery('');
                      setShowCameraDropdown(false);
                    }}
                    disabled={!selectedFormLocation}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  >
                    <option value="">{t('camera.formSelectNvr')}</option>
                    {selectedFormLocation && getNvrsForLocation(selectedFormLocation).map(nvr => (
                      <option key={nvr.id} value={nvr.id}>
                        {nvr.name} ({nvr.ip_address})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Kamera Seçimi - Arama ile */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    📷 {t('camera.colCamera')} {selectedFormNvr ? '' : t('camera.formCameraSelectNvr')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('camera.formCameraPlaceholder')}
                      value={formData.camera_id
                        ? getCamerasForNvr(selectedFormNvr || 0).find(c => c.id.toString() === formData.camera_id)
                          ? `${getCamerasForNvr(selectedFormNvr || 0).find(c => c.id.toString() === formData.camera_id)?.brand} ${getCamerasForNvr(selectedFormNvr || 0).find(c => c.id.toString() === formData.camera_id)?.model}`
                          : cameraSearchQuery
                        : cameraSearchQuery
                      }
                      onChange={(e) => {
                        setCameraSearchQuery(e.target.value);
                        setShowCameraDropdown(true);
                      }}
                      onFocus={() => selectedFormNvr && setShowCameraDropdown(true)}
                      disabled={!selectedFormNvr}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                    />

                    {/* Dropdown Liste */}
                    {showCameraDropdown && selectedFormNvr && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {/* Seçimi Temizle */}
                        {formData.camera_id && (
                          <div
                            onClick={() => {
                              setFormData({
                                ...formData,
                                camera_id: '',
                                camera_name: '',
                                ip_address: ''
                              });
                              setCameraSearchQuery('');
                              setShowCameraDropdown(false);
                              // Görüntüyü de temizle
                              setPreviewImage(null);
                              setUploadedImage(null);
                            }}
                            className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-700 dark:border-gray-600"
                          >
                            {t('camera.formCameraClearSelection')}
                          </div>
                        )}

                        {/* Seçenek Yok */}
                        {getFilteredCameras().length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {getCamerasForNvr(selectedFormNvr).length === 0
                              ? t('camera.formNoNvrCameras')
                              : t('camera.formNoCamerasMatch')}
                          </div>
                        )}

                        {/* Kamera Listesi */}
                        {getFilteredCameras().map(camera => (
                          <div
                            key={camera.id}
                            onClick={() => {
                              setFormData({
                                ...formData,
                                camera_id: camera.id.toString(),
                                camera_name: `${camera.brand} ${camera.model}`,
                                ip_address: camera.ip_address
                              });
                              setCameraSearchQuery('');
                              setShowCameraDropdown(false);
                              // Kameranın screenshot'ı varsa otomatik olarak göster
                              if (camera.screenshot_path) {
                                setPreviewImage(`/uploads/${camera.screenshot_path}`);
                                setUploadedImage(null);
                              }
                            }}
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-primary/20 dark:hover:bg-primary/90 ${
                              formData.camera_id === camera.id.toString()
                                ? 'bg-primary/10 dark:bg-primary/90 border-l-4 border-primary'
                                : ''
                            }`}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1">
                              {camera.brand} {camera.model}
                              {camera.screenshot_path && (
                                <ImageIcon className="w-3 h-3 text-green-500" title="Görüntü mevcut" />
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              IP: {camera.ip_address} | Kanal: {camera.id}
                              {camera.screenshot_path && <span className="text-green-500 ml-1">📷</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {formData.camera_id && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      {t('camera.formCameraSelected', { name: formData.camera_name })}
                    </p>
                  )}
                </div>

                {/* Kamera Adı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('camera.formLabelCameraName')}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.camera_name}
                    onChange={(e) => setFormData({...formData, camera_name: e.target.value})}
                    placeholder={t('camera.formCameraNamePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* IP Adresi - ReadOnly */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('camera.formLabelIp')}
                  </label>
                  <input
                    type="text"
                    value={formData.ip_address}
                    readOnly={!!formData.camera_id}
                    onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                    placeholder="192.168.1.100"
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white ${
                      formData.camera_id ? 'bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 cursor-not-allowed' : ''
                    }`}
                  />
                  {formData.camera_id && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('camera.formIpReadonlyNote')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('camera.formLabelArea')}
                  </label>
                  <input
                    type="text"
                    value={formData.location_info}
                    onChange={(e) => setFormData({...formData, location_info: e.target.value})}
                    placeholder={t('camera.formAreaPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('camera.formLabelReportedBy')}
                  </label>
                  <input
                    type="text"
                    value={formData.reported_by_name}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 dark:text-white text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('camera.formLabelStatus')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="açık">{t('camera.formStatusOpen')}</option>
                    <option value="inceleniyor">{t('camera.formStatusReviewing')}</option>
                    <option value="çözümlendi">{t('camera.formStatusResolved')}</option>
                    <option value="kapalı">{t('camera.formStatusClosed')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('camera.formLabelFaultDesc')}
                </label>
                <select
                  required
                  value={formData.fault_description}
                  onChange={(e) => setFormData({...formData, fault_description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                >
                  <option value="">{t('camera.formFaultDescSelect')}</option>
                  {faultDescriptionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('camera.formLabelNotes')}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  placeholder={t('camera.formNotesPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>


              {/* Resim Yükleme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('camera.formLabelLastImage')}
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  {previewImage ? (
                    <div className="space-y-2">
                      <img
                        src={previewImage}
                        alt="Resim Önizlemesi"
                        className="w-full max-h-48 object-cover rounded"
                        onError={(e) => {
                          console.error('Image failed to load:', previewImage);
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewImage(null);
                          setUploadedImage(null);
                        }}
                        className="w-full px-3 py-2 text-sm text-red-600 hover:text-red-900 border border-red-300 rounded-lg"
                      >
                        {t('camera.formImageRemove')}
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center justify-center py-8">
                        <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('camera.formImageUpload')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400 mt-1">
                          {t('camera.formImageHint')}
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 flex justify-end gap-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Tedarikçi Atama Modal */}
      <Modal isOpen={showSupplierModal} onClose={() => setShowSupplierModal(false)}>
          {editingFault && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('camera.supplierTitle')}
              </h2>
              <button type="button" onClick={() => setShowSupplierModal(false)} className="-mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={t('common.close')}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <strong>{t('camera.supplierLabelCamera')}</strong> {editingFault.camera_name}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <strong>{t('camera.supplierLabelFault')}</strong> {editingFault.fault_description}
              </p>
              {editingFault.location_name && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>{t('camera.supplierLabelLocation')}</strong> {editingFault.location_name}
                </p>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('camera.supplierLabelSelect')}
                </label>
                <select
                  value={selectedSupplier || ''}
                  onChange={(e) => setSelectedSupplier(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">{t('camera.supplierSelectDefault')}</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                {suppliers.length === 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {t('camera.supplierNoFound')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('camera.supplierLabelNotes')}
                </label>
                <textarea
                  value={supplierNotes}
                  onChange={(e) => setSupplierNotes(e.target.value)}
                  rows={3}
                  placeholder={t('camera.supplierNotesPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSupplierModal(false)}
                  className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={submitAssignSupplier}
                  className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2 min-h-[44px] bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  disabled={!selectedSupplier}
                >
                  <Truck className="w-4 h-4 mr-2" />
                  {t('camera.supplierBtnAssign')}
                </button>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Konsinye Kamera Atama Modalı */}
      <Modal isOpen={showConsignmentModal} onClose={() => setShowConsignmentModal(false)}>
        {consignmentFault && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">{t('camera.consignmentTitle')}</h2>
                  <p className="text-teal-100 text-sm">{consignmentFault.camera_name}</p>
                </div>
              </div>
              <button onClick={() => setShowConsignmentModal(false)} className="-mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors" aria-label={t('common.close')}>
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {/* Arızalı kamera bilgisi */}
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                <p className="text-xs text-red-600 dark:text-red-400 font-medium mb-1">{t('camera.consignmentFaultyCameraLabel')}</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{consignmentFault.camera_name}</p>
                {(consignmentFault.camera_brand || consignmentFault.camera_model) && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">{consignmentFault.camera_brand} {consignmentFault.camera_model}</p>
                )}
                {consignmentFault.location_name && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{consignmentFault.location_name}</p>
                )}
              </div>

              {/* Konsinye kamera seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <ArrowLeftRight className="w-4 h-4 inline mr-1" />
                  {t('camera.consignmentLabelSelect')}
                </label>
                <input
                  type="text"
                  placeholder={t('camera.consignmentSearchPlaceholder')}
                  value={consignmentSearch}
                  onChange={(e) => {
                    setConsignmentSearch(e.target.value);
                    setSelectedConsignmentCamera(null);
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                {consignmentSearch.length >= 2 && !selectedConsignmentCamera && (
                  <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto shadow-lg bg-white dark:bg-gray-700">
                    {cameras
                      .filter(c =>
                        c.id !== consignmentFault.camera_id &&
                        (
                          c.alias?.toLowerCase().includes(consignmentSearch.toLowerCase()) ||
                          c.brand?.toLowerCase().includes(consignmentSearch.toLowerCase()) ||
                          c.model?.toLowerCase().includes(consignmentSearch.toLowerCase()) ||
                          c.ip_address?.includes(consignmentSearch)
                        )
                      )
                      .slice(0, 15)
                      .map(cam => (
                        <button
                          key={cam.id}
                          onClick={() => {
                            setSelectedConsignmentCamera(cam);
                            setConsignmentSearch(cam.alias || `${cam.brand} ${cam.model}`);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-teal-50 dark:hover:bg-teal-900/30 text-sm border-b border-gray-100 dark:border-gray-600 last:border-0"
                        >
                          <p className="font-medium text-gray-900 dark:text-gray-100">{cam.alias || `${cam.brand} ${cam.model}`}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{cam.brand} {cam.model} · {cam.ip_address || t('camera.consignmentNoIp')} · {cam.nvr_name}</p>
                        </button>
                      ))}
                    {cameras.filter(c =>
                      c.id !== consignmentFault.camera_id &&
                      (
                        c.alias?.toLowerCase().includes(consignmentSearch.toLowerCase()) ||
                        c.brand?.toLowerCase().includes(consignmentSearch.toLowerCase()) ||
                        c.model?.toLowerCase().includes(consignmentSearch.toLowerCase()) ||
                        c.ip_address?.includes(consignmentSearch)
                      )
                    ).length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{t('camera.consignmentNoFound')}</p>
                    )}
                  </div>
                )}
                {selectedConsignmentCamera && (
                  <div className="mt-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3 border border-teal-200 dark:border-teal-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm text-teal-800 dark:text-teal-200">{selectedConsignmentCamera.alias || `${selectedConsignmentCamera.brand} ${selectedConsignmentCamera.model}`}</p>
                        <p className="text-xs text-teal-600 dark:text-teal-400">{selectedConsignmentCamera.brand} {selectedConsignmentCamera.model} · {selectedConsignmentCamera.ip_address}</p>
                        <p className="text-xs text-teal-500 dark:text-teal-500">{selectedConsignmentCamera.nvr_name}</p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                    </div>
                  </div>
                )}
              </div>

              {/* Notlar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('camera.consignmentLabelNotes')}</label>
                <textarea
                  value={consignmentNotes}
                  onChange={(e) => setConsignmentNotes(e.target.value)}
                  rows={2}
                  placeholder={t('camera.consignmentNotesPlaceholder')}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowConsignmentModal(false)}
                  className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={submitConsignment}
                  disabled={!selectedConsignmentCamera || consignmentSubmitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Package className="w-4 h-4" />
                  {consignmentSubmitting ? t('camera.consignmentBtnPlacing') : t('camera.consignmentBtnPlace')}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Konsinye İade Modal */}
      <Modal isOpen={showReturnModal} onClose={() => setShowReturnModal(false)}>
        {returnFault && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">{t('camera.returnTitle')}</h2>
                  <p className="text-blue-100 text-sm">{returnFault.camera_name}</p>
                </div>
              </div>
              <button onClick={() => setShowReturnModal(false)} className="-mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors" aria-label={t('common.close')}>
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {/* Konsinye kamera bilgisi */}
              {returnFault.consignment_camera_name && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2">{t('camera.returnActiveLabel')}</p>
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{returnFault.consignment_camera_name}</p>
                      {(returnFault.consignment_camera_brand || returnFault.consignment_camera_model) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{returnFault.consignment_camera_brand} {returnFault.consignment_camera_model}</p>
                      )}
                      {returnFault.consignment_assigned_at && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('camera.returnPlacedAt', { date: new Date(returnFault.consignment_assigned_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') })}
                        </p>
                      )}
                      {returnFault.consignment_assigned_by && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('camera.returnBy', { name: returnFault.consignment_assigned_by })}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-300">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  {t('camera.returnConfirmPre')}<strong>{t('camera.returnConfirmStrong')}</strong>{t('camera.returnConfirmPost')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('camera.returnLabelNotes')}</label>
                <textarea
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  rows={3}
                  placeholder={t('camera.returnNotesPlaceholder')}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={submitReturn}
                  disabled={returnSubmitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className="w-4 h-4" />
                  {returnSubmitting ? t('camera.returnBtnProcessing') : t('camera.returnBtnReturn')}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Arıza Detay Modalı */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {faultDetails && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-white">{t('camera.detailTitle')}</h2>
                  {faultDetails?.fault && (
                    <p className="text-purple-200 text-sm break-words">#{faultDetails.fault.id} - {faultDetails.fault.camera_name}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setFaultDetails(null);
                }}
                className="flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors"
                aria-label={t('common.close')}
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : faultDetails ? (
                <div className="space-y-6">
                  {/* Kamera Görseli */}
                  {(faultDetails.fault.camera_screenshot_path || faultDetails.fault.image_path) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {faultDetails.fault.camera_screenshot_path && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                            <Camera className="w-3.5 h-3.5" /> {t('camera.detailImageCamera')}
                          </p>
                          <img
                            src={`/uploads/${faultDetails.fault.camera_screenshot_path}`}
                            alt="Kamera görüntüsü"
                            className="w-full max-h-52 object-cover rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setSelectedImageForModal(`/uploads/${faultDetails.fault.camera_screenshot_path}`)}
                          />
                        </div>
                      )}
                      {faultDetails.fault.image_path && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5 text-orange-500" /> {t('camera.detailImageFault')}
                          </p>
                          <img
                            src={`/uploads/${faultDetails.fault.image_path}`}
                            alt="Arıza fotoğrafı"
                            className="w-full max-h-52 object-cover rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setSelectedImageForModal(`/uploads/${faultDetails.fault.image_path}`)}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Arıza ve Cihaz Bilgileri */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Cihaz Bilgileri */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Camera className="w-5 h-5 text-primary dark:text-primary" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionDevice')}</h3>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelCameraName')}</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{faultDetails.fault.camera_name}</span>
                        </div>
                        {faultDetails.fault.camera_brand && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelBrandModel')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {faultDetails.fault.camera_brand} {faultDetails.fault.camera_model}
                            </span>
                          </div>
                        )}
                        {faultDetails.fault.ip_address && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelIp')}</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100">{faultDetails.fault.ip_address}</span>
                          </div>
                        )}
                        {faultDetails.fault.nvr_name && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelNvr')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{faultDetails.fault.nvr_name}</span>
                          </div>
                        )}
                        {faultDetails.fault.channel_number && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelChannel')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{faultDetails.fault.channel_number}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Lokasyon Bilgileri */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Building2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionLocation')}</h3>
                      </div>
                      <div className="space-y-3 text-sm">
                        {faultDetails.fault.location_name && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelLocation')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{faultDetails.fault.location_name}</span>
                          </div>
                        )}
                        {faultDetails.fault.location_info && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelArea')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{faultDetails.fault.location_info}</span>
                          </div>
                        )}
                        {faultDetails.fault.location_address && (
                          <div className="flex justify-between gap-3">
                            <span className="flex-shrink-0 text-gray-500 dark:text-gray-400">{t('camera.detailLabelAddress')}</span>
                            <span className="min-w-0 font-medium text-gray-900 dark:text-gray-100 text-right break-words">{faultDetails.fault.location_address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Arıza Bilgileri */}
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-5 border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionFault')}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelFaultType')}</span>
                        <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{faultDetails.fault.fault_description}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelStatus')}</span>
                        <p className="mt-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${statusColors[faultDetails.fault.status]?.bg} ${statusColors[faultDetails.fault.status]?.text}`}>
                            {statusColors[faultDetails.fault.status]?.icon}
                            {faultDetails.fault.status.charAt(0).toUpperCase() + faultDetails.fault.status.slice(1)}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelReporter')}</span>
                        <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{faultDetails.fault.reported_by_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelReportDate')}</span>
                        <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                          {new Date(faultDetails.fault.reported_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </p>
                      </div>
                      {faultDetails.fault.notes && (
                        <div className="md:col-span-2">
                          <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelNotes')}</span>
                          <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{faultDetails.fault.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tedarikçi Bilgileri */}
                  {faultDetails.fault.supplier_name && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-5 border border-orange-200 dark:border-orange-800">
                      <div className="flex items-center gap-2 mb-4">
                        <Truck className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionSupplier')}</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelCompany')}</span>
                          <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{faultDetails.fault.supplier_name}</p>
                        </div>
                        {faultDetails.fault.supplier_contact_person && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelContact')}</span>
                            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{faultDetails.fault.supplier_contact_person}</p>
                          </div>
                        )}
                        {faultDetails.fault.supplier_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 dark:text-gray-100">{faultDetails.fault.supplier_phone}</span>
                          </div>
                        )}
                        {faultDetails.fault.supplier_email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 dark:text-gray-100">{faultDetails.fault.supplier_email}</span>
                          </div>
                        )}
                        {faultDetails.fault.assigned_at && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelAssignDate')}</span>
                            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                              {new Date(faultDetails.fault.assigned_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Konsinye Kayıtları */}
                  {faultDetails.consignmentRecords && faultDetails.consignmentRecords.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 mb-4">
                        <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionConsignment')}</h3>
                      </div>
                      <div className="space-y-4">
                        {faultDetails.consignmentRecords.map((rec) => (
                          <div key={rec.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                            <div className="flex items-center justify-between mb-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${rec.status === 'aktif' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                                {rec.status === 'aktif' ? <Package className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                                {rec.status === 'aktif' ? t('camera.detailConsignmentActive') : t('camera.detailConsignmentReturned')}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">#{rec.id}</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelFaultyCamera')}</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{rec.faulty_camera_name}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelConsignmentCamera')}</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{rec.consignment_camera_name}</p>
                                {rec.consignment_camera_brand && <p className="text-xs text-gray-500 dark:text-gray-400">{rec.consignment_camera_brand} {rec.consignment_camera_model}</p>}
                              </div>
                              {rec.consignment_camera_ip && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelIp')}</span>
                                  <p className="font-mono text-gray-900 dark:text-gray-100">{rec.consignment_camera_ip}</p>
                                </div>
                              )}
                              {rec.consignment_camera_inventory && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelInventory')}</span>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{rec.consignment_camera_inventory}</p>
                                </div>
                              )}
                              {rec.con_nvr_name && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelNvr')}</span>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{rec.con_nvr_name}</p>
                                </div>
                              )}
                              {rec.con_location_name && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelLocation')}</span>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{rec.con_location_name}</p>
                                </div>
                              )}
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelPlacedAt')}</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{new Date(rec.assigned_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</p>
                                {rec.assigned_by_name && <p className="text-xs text-gray-500 dark:text-gray-400">{t('camera.detailByName', { name: rec.assigned_by_name })}</p>}
                              </div>
                              {rec.returned_at && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelReturnedAt')}</span>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{new Date(rec.returned_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</p>
                                  {rec.returned_by_name && <p className="text-xs text-gray-500 dark:text-gray-400">{t('camera.detailByName', { name: rec.returned_by_name })}</p>}
                                </div>
                              )}
                              {rec.consignment_notes && (
                                <div className="sm:col-span-2">
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelConsignNotes')}</span>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{rec.consignment_notes}</p>
                                </div>
                              )}
                              {rec.return_notes && (
                                <div className="sm:col-span-2">
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">{t('camera.detailLabelReturnNotes')}</span>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{rec.return_notes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Çözüm Bilgileri */}
                  {faultDetails.solution && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-5 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 mb-4">
                        <Wrench className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionSolution')}</h3>
                      </div>
                      <div className="space-y-4 text-sm">
                        {faultDetails.solution.resolvedBy && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelResolvedBy')}</span>
                            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{faultDetails.solution.resolvedBy}</p>
                          </div>
                        )}
                        {faultDetails.solution.resolvedAt && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelResolvedAt')}</span>
                            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                              {new Date(faultDetails.solution.resolvedAt).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                            </p>
                          </div>
                        )}
                        {faultDetails.solution.description && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelFaultDescLabel')}</span>
                            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1 bg-white dark:bg-gray-800 p-3 rounded-lg">
                              {faultDetails.solution.description}
                            </p>
                          </div>
                        )}
                        {faultDetails.solution.solutionText && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelSolutionDesc')}</span>
                            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1 bg-white dark:bg-gray-800 p-3 rounded-lg">
                              {faultDetails.solution.solutionText}
                            </p>
                          </div>
                        )}
                        {faultDetails.solution.filePath && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">{t('camera.detailLabelAttachedFile')}</span>
                            <div className="mt-2">
                              <img
                                src={`/uploads/${faultDetails.solution.filePath}`}
                                alt="Çözüm fotoğrafı"
                                className="max-w-xs rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-80"
                                onClick={() => setSelectedImageForModal(`/uploads/${faultDetails.solution?.filePath}`)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tedarikçi Aksiyonları (İşlem Geçmişi) */}
                  {faultDetails.supplierActions && faultDetails.supplierActions.length > 0 && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-5 border border-indigo-200 dark:border-indigo-800">
                      <div className="flex items-center gap-2 mb-4">
                        <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionSupplierHistory')}</h3>
                      </div>
                      <div className="space-y-4">
                        {faultDetails.supplierActions.map((action, index) => (
                          <div
                            key={action.id}
                            className={`relative pl-6 pb-4 ${index !== faultDetails.supplierActions.length - 1 ? 'border-l-2 border-indigo-200 dark:border-indigo-700' : ''}`}
                          >
                            <div className="absolute left-[-8px] top-0 w-4 h-4 rounded-full bg-indigo-500 border-2 border-white dark:border-gray-800"></div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                  {action.action_type === 'status_updated' ? t('camera.detailActionStatusUpdated') :
                                   action.action_type === 'note_added' ? t('camera.detailActionNoteAdded') :
                                   action.action_type === 'file_uploaded' ? t('camera.detailActionFileUploaded') : action.action_type}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(action.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300">
                                <p><strong>{t('camera.detailLabelPerformedBy')}</strong> {action.supplier_user_name || action.supplier_company_name || t('camera.detailUnknown')}</p>
                                {action.old_status && action.new_status && (
                                  <p className="mt-1">
                                    <strong>{t('camera.detailLabelStatusChange')}</strong> {action.old_status} → {action.new_status}
                                  </p>
                                )}
                                {action.description && (
                                  <p className="mt-1"><strong>{t('camera.detailLabelDescription')}</strong> {action.description}</p>
                                )}
                                {action.solution_text && (
                                  <p className="mt-1"><strong>{t('camera.detailLabelSolution')}</strong> {action.solution_text}</p>
                                )}
                              </div>
                              {action.file_path && (
                                <div className="mt-2">
                                  <img
                                    src={`/uploads/${action.file_path}`}
                                    alt="İşlem fotoğrafı"
                                    className="max-w-[150px] rounded border cursor-pointer hover:opacity-80"
                                    onClick={() => setSelectedImageForModal(`/uploads/${action.file_path}`)}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sistem Logları */}
                  {faultDetails.logs && faultDetails.logs.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('camera.detailSectionLogs')}</h3>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {faultDetails.logs.map((log) => (
                          <div key={log.id} className="flex items-start gap-3 text-sm p-2 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 rounded">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                              log.user_type === 'admin' ? 'bg-primary' :
                              log.user_type === 'supplier' ? 'bg-orange-500' : 'bg-gray-500'
                            }`}></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-900 dark:text-gray-100">{log.description || log.action_type}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {log.user_name || log.admin_user_name || log.supplier_user_name_from_table || t('camera.detailSystem')} •{' '}
                                {new Date(log.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('camera.detailNoDetails')}</p>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default CameraFaultsPage;
